import type { Column, RowData } from '@tanstack/react-table'
import type { ChangeEvent, ReactElement } from 'react'
import { DateInput } from '~/components/Form/Input/DateInput'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { TextInput } from '~/components/Form/Input/TextInput'
import { parseDateOrNull, toISOLocal } from '~/utils/date'
import { filterWrapper } from './DataTable.css'
import type { DataTableFeatures } from './dataTableFeatures'

type DataTableHeaderFilterProps<T extends RowData> = {
  column: Column<DataTableFeatures, T, unknown>
}

const DataTableHeaderFilter = <T extends RowData>({
  column
}: DataTableHeaderFilterProps<T>): ReactElement | null => {
  if (!column.getCanFilter() && !column.columnDef.enableColumnFilter)
    return null

  const meta = column.columnDef.meta
  const filterType = meta?.filterType
  const filterValue = column.getFilterValue() as string | undefined
  const setFilter = (val: string | undefined) => column.setFilterValue(val)

  if (meta?.filterComponent) {
    return (
      <div className={filterWrapper} onClick={(e) => e.stopPropagation()}>
        {meta.filterComponent(filterValue, setFilter)}
      </div>
    )
  }

  if (filterType === 'boolean') {
    return (
      <div className={filterWrapper} onClick={(e) => e.stopPropagation()}>
        <Dropdown
          key={filterValue ?? ''}
          options={[
            {
              label: meta?.filterBooleanText?.trueOption ?? 'Oui',
              value: 'true'
            },
            {
              label: meta?.filterBooleanText?.falseOption ?? 'Non',
              value: 'false'
            }
          ]}
          emptyLabel={meta?.filterBooleanText?.emptyOption ?? 'Tous'}
          defaultValue={filterValue === 'all' ? '' : (filterValue ?? '')}
          extraSmall
          fullWidth
          onChange={(value) => column.setFilterValue(value || 'all')}
        />
      </div>
    )
  }

  if (filterType === 'list') {
    return (
      <div className={filterWrapper} onClick={(e) => e.stopPropagation()}>
        <Dropdown
          key={filterValue ?? ''}
          options={meta?.filterListOptions ?? []}
          emptyLabel={meta?.filterListEmptyLabel ?? 'Tous'}
          defaultValue={filterValue ?? ''}
          extraSmall
          fullWidth
          searchable={meta?.filterListSearchable}
          onChange={(value) => column.setFilterValue(value || undefined)}
        />
      </div>
    )
  }

  if (filterType === 'date') {
    return (
      <div className={filterWrapper} onClick={(e) => e.stopPropagation()}>
        <DateInput
          wrapper={false}
          fullWidth
          leadingText="Depuis le"
          inputSize="extraSmall"
          defaultDate={parseDateOrNull(filterValue)}
          onChange={(date) =>
            column.setFilterValue(
              date ? toISOLocal(date).slice(0, 10) : undefined
            )
          }
        />
      </div>
    )
  }

  return (
    <TextInput
      standalone
      wrapper={false}
      inputSize="extraSmall"
      noWrapperClassName={filterWrapper}
      type="search"
      placeholder={meta?.filterPlaceholder ?? 'Filtrer...'}
      value={filterValue ?? ''}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        column.setFilterValue(e.target.value || undefined)
      }
      onClick={(e) => e.stopPropagation()}
    />
  )
}

export default DataTableHeaderFilter
